import { useRef } from "react";
import Slider from "react-slick";
import { MdChevronLeft, MdChevronRight } from "react-icons/md";
import useIsDesktop from "@/hooks/useIsDesktop";
import useMobileScrollDots from "@/hooks/useMobileScrollDots";
import useSlickArrows from "@/hooks/useSlickArrows";
import styles from "./MostBookedRoutes.module.css";
import dayjs from "dayjs";


function RouteCard({ route }) {
  return (
    <a href={route.href} className={styles.card}>
      <div className={styles.imageWrap}>
        <img src={route.image} alt={route.name} className={styles.cardImage} />
      </div>
      <div className={styles.label}>
        <span>{route.name}</span>
      </div>
    </a>
  );
}

export default function MostBookedRoutes({ sso }) {
  const fromDate = dayjs().add(15, 'day').format('DD-MMM-YYYY');
  const toDate = dayjs().add(20, 'day').format('DD-MMM-YYYY');
  const ROUTES = [
    {
      id: "bali",
      name: "Bali",
      image: "https://blueberry.azureedge.net/nhfrontend/Flights/Bali.webp",
      href: `https://book.nextholidays.com/AE-EN/flight/listing/tf?dep1=DXB&ret1=DPS&dtt1=${fromDate}&cl1=Y&dep2=DPS&ret2=DXB&dtt2=${toDate}&cl2=Y&triptype=2&adult=1&child=0&infant=0&direct=false&baggage=false&key=IRT&airlines=&ref=false&lc=EN&curr=AED&mgcc=AE&ipc=false&umrah=false&ndc=false&sso=${sso}`,
    },
    {
      id: "egypt",
      name: "Egypt",
      image: "https://blueberry.azureedge.net/nhfrontend/Flights/Egypt.webp",
      href: `https://book.nextholidays.com/AE-EN/flight/listing/tf?dep1=DXB&ret1=CAI&dtt1=${fromDate}&cl1=Y&dep2=CAI&ret2=DXB&dtt2=${toDate}&cl2=Y&triptype=2&adult=1&child=0&infant=0&direct=false&baggage=false&key=IRT&airlines=&ref=false&lc=EN&curr=AED&mgcc=AE&ipc=false&umrah=false&ndc=false&sso=${sso}`,
    },
    {
      id: "morocco",
      name: "Morocco",
      image: "https://blueberry.azureedge.net/nhfrontend/Flights/Morocco.webp",
      href: `https://book.nextholidays.com/AE-EN/flight/listing/tf?dep1=DXB&ret1=CMN&dtt1=${fromDate}&cl1=Y&dep2=CMN&ret2=DXB&dtt2=${toDate}&cl2=Y&triptype=2&adult=1&child=0&infant=0&direct=false&baggage=false&key=IRT&airlines=&ref=false&lc=EN&curr=AED&mgcc=AE&ipc=false&umrah=false&ndc=false&sso=${sso}`,
    },
    {
      id: "london",
      name: "London",
      image: "https://blueberry.azureedge.net/nhfrontend/Flights/London.webp",
      href: "/flights/london",
    },
    {
      id: "saudi-arabia",
      name: "Saudi Arabia",
      image: "https://blueberry.azureedge.net/nhfrontend/Flights/saudi%20arabia.webp",
      href: "/flights/saudi-arabia",
    },
    {
      id: "istanbul",
      name: "Istanbul",
      image: "https://blueberry.azureedge.net/nhfrontend/Flights/Istanbul.webp",
      href: "/flights/istanbul",
    },
    {
      id: "thailand",
      name: "Thailand",
      image: "https://blueberry.azureedge.net/nhfrontend/Flights/Thailand.webp",
      href: "/flights/thailand",
    },
    {
      id: "delhi",
      name: "Delhi",
      image: "https://blueberry.azureedge.net/nhfrontend/Flights/Delhi.webp",
      href: "/flights/delhi",
    },
    {
      id: "mumbai",
      name: "Mumbai",
      image: "https://blueberry.azureedge.net/nhfrontend/Flights/Mumbai.webp",
      href: "/flights/mumbai",
    },
    {
      id: "japan",
      name: "Japan",
      image: "https://blueberry.azureedge.net/nhfrontend/Flights/Japan.webp",
      href: "/flights/japan",
    },
    {
      id: "georgia",
      name: "Georgia",
      image: "https://blueberry.azureedge.net/nhfrontend/Flights/Georgia.webp",
      href: "/flights/georgia",
    },
    {
      id: "kazakhstan",
      name: "Kazakhstan",
      image: "https://blueberry.azureedge.net/nhfrontend/Flights/kazakhstan.webp",
      href: "/flights/kazakhstan",
    },
  ];
  const { isDesktop, mounted } = useIsDesktop(900);
  const sliderRef = useRef(null);
  const slidesToShow = 6;
  const { scrollRef, activeDot, mobileDots, onScroll, goToDot } = useMobileScrollDots(
    ROUTES.length,
    ROUTES.length
  );


  const { canPrev, canNext, slickArrowEvents } = useSlickArrows(
    ROUTES.length,
    slidesToShow
  );

  const slickSettings = {
    dots: false,
    infinite: false,
    speed: 400,
    slidesToShow,
    slidesToScroll: 6,
    arrows: false,
    centerMode: false,
    swipe: false,
    draggable: false,
    touchMove: false,
    ...slickArrowEvents,
    responsive: [
      {
        breakpoint: 1200,
        settings: {
          slidesToShow: 4,
          slidesToScroll: 4,
          swipe: false,
          draggable: false,
          touchMove: false,
        },
      },
    ],
  };

  const showSlick = mounted && isDesktop;
  const fewSlides = ROUTES.length <= slidesToShow;

  const routeSlides = ROUTES.map((route) => (
    <div key={route.id} className={styles.slickSlide}>
      <RouteCard route={route} />
    </div>
  ));

  return (
    <section className={`${styles.section} standard-padding`}>
      <div className={styles.header}>
        <div className={styles.headingBlock}>
          <h2 className="standard-heading">Flight Destinations</h2>
          <p className="standard-subheading">
            Find top destinations and book flights at the best fares.
          </p>
        </div>

        {showSlick ? (
          <div className="headerArrows">
            <button
              type="button"
              className="headerArrowBtn"
              aria-label="Previous"
              disabled={!canPrev}
              onClick={() => {
                if (typeof sliderRef.current?.slickPrev === "function") {
                  sliderRef.current.slickPrev();
                }
              }}
            >
              <MdChevronLeft size={22} />
            </button>
            <button
              type="button"
              className="headerArrowBtn"
              aria-label="Next"
              disabled={!canNext}
              onClick={() => {
                if (typeof sliderRef.current?.slickNext === "function") {
                  sliderRef.current.slickNext();
                }
              }}
            >
              <MdChevronRight size={22} />
            </button>
          </div>
        ) : null}
      </div>

      {showSlick ? (
        <div className={`${styles.slickWrap} ${fewSlides ? styles.alignLeft : ""}`}>
          <Slider ref={sliderRef} {...slickSettings}>
            {routeSlides}
          </Slider>
        </div>
      ) : (
        <>
          <div ref={scrollRef} className={styles.slider} onScroll={onScroll}>
            {ROUTES.map((route) => (
              <RouteCard key={route.id} route={route} />
            ))}
          </div>

          {mobileDots > 1 ? (
            <div className={styles.dots} aria-label="Slides">
              {Array.from({ length: mobileDots }).map((_, index) => (
                <button
                  key={index}
                  type="button"
                  className={`${styles.dot} ${activeDot === index ? styles.dotActive : ""}`}
                  aria-label={`Go to slide ${index + 1}`}
                  onClick={() => goToDot(index)}
                />
              ))}
            </div>
          ) : null}
        </>
      )}
    </section>
  );
}
